import { getBaseCurrency } from "@/constant/currency"
import { liveLobbyOf } from "@/constant/liveLobby"
import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { t } from 'i18next'
import api from '@/core/services/api'
import useFormData from '@/core/hooks/useFormData'
import useTableData from '@/core/hooks/useTableData'
import { setMenuData } from '@/core/store/menu/actions'
import { useCommonState } from '@/core/context/useContext'
import { usePopup } from '@/core/context/useWindow'
import Login from '@/components/popup/Login'
import { assetThumb } from '@/core/services/thumbUrl'
import * as S from './styled'

// 카지노(LIVE) 클릭 시 로비 직접 이동 — 일본(그랜드라인) 빌드에서만 적용
const LIVE_LOBBY_ENABLED = process.env.REACT_APP_LIVE_LOBBY === 'true'

export default function MGameList() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isLogin } = useCommonState()
  const { openPopup } = usePopup()
  const menuData = useSelector((state) => state.Menu)

  const tg = useTableData(true)
  useFormData({}, () => {
    api.get('/game/side-menu').then((res) => {
      dispatch(setMenuData(res?.data?.content?.side_menu || []))
    }).catch(console.error)
  }, tg)

  // side_menu 최상위가 [01_02, 01_01] 형태 또는 [01 > subMenus] 형태 모두 대응
  const findNode = (code) =>
    menuData?.find?.(m => m.menuCode === code) ||
    menuData?.[0]?.subMenus?.find?.(m => m.menuCode === code)
  const slotVendors = findNode('01_02')?.subMenus || []
  const liveVendors = findNode('01_01')?.subMenus || []

  // 순서는 무조건 슬롯(01_02_X) sort 기준 — 슬롯에 게임이 없어도(hasGame=0) 노드는 받으므로 순서 유지.
  // hasSlot/hasLive 는 hasGame=1 일 때만 true, 둘 다 false면 카드 제외.
  const mergedVendors = useMemo(() => {
    const map = new Map()

    slotVendors.forEach(v => {
      if (!map.has(v.name)) {
        map.set(v.name, { name: v.name, thumbnail: v.mobileThumbnail || v.thumbnail, mobileThumb: !!v.mobileThumbnail, sort: v.sort, menuCode: v.menuCode, hasSlot: false, hasLive: false })
      }
      const entry = map.get(v.name)
      if (!entry.thumbnail) { entry.thumbnail = v.mobileThumbnail || v.thumbnail; entry.mobileThumb = !!v.mobileThumbnail }
      if (v.hasGame) { entry.hasSlot = true; entry.menuCode = v.menuCode }
    })

    liveVendors.forEach(v => {
      // 슬롯 노드가 없을 때만 라이브 sort(+1000) 기준으로 신규 등록
      if (!map.has(v.name)) {
        map.set(v.name, { name: v.name, thumbnail: v.mobileThumbnail || v.thumbnail, mobileThumb: !!v.mobileThumbnail, sort: v.sort + 1000, menuCode: v.menuCode, hasSlot: false, hasLive: false })
      }
      const entry = map.get(v.name)
      if (!entry.thumbnail) { entry.thumbnail = v.mobileThumbnail || v.thumbnail; entry.mobileThumb = !!v.mobileThumbnail }
      if (v.hasGame) {
        entry.hasLive = true
        // 슬롯 진입점이 없으면 라이브 menuCode가 진입점
        if (!entry.hasSlot) entry.menuCode = v.menuCode
      }
    })

    return Array.from(map.values())
      .filter(entry => entry.hasSlot || entry.hasLive)
      .sort((a, b) => a.sort - b.sort)
  }, [slotVendors, liveVendors])

  // gold 레이아웃에는 카테고리 필터 탭이 없다 — 전체 벤더를 그대로 보여준다.
  const visibleVendors = mergedVendors

  const handleClick = (vendor) => {
    const isLiveOnly = vendor.hasLive && !vendor.hasSlot
    if (!isLiveOnly) {
      navigate(`/m/game-list/${vendor.menuCode}`)
      return
    }
    if (!isLogin) {
      openPopup({ grandLogin: true, title: t('modalTitle.login'), content: <Login isMobile /> })
      return
    }
    // 일본(그랜드라인) 빌드에서만 로비 직접 이동
    if (LIVE_LOBBY_ENABLED) {
      const entry = liveLobbyOf(vendor.name)
      if (entry) {
        // 게임은 새 창으로 연다(2026-10-23 요청)
        window.open(`/m/game-play/${entry.vendor}/${entry.id}/${getBaseCurrency()}`, '_blank')
        return
      }
    }
    navigate(`/m/game-list/${vendor.menuCode}`)
  }

  if (!mergedVendors.length) return null

  // gold8.bet 모바일: 둥근 어두운 판 안에 게임사 타일 3열
  return (
    <S.Panel>
      <S.Grid>
        {visibleVendors.map((vendor) => (
          <S.Tile key={vendor.name} $mobileThumb={vendor.mobileThumb} onClick={() => handleClick(vendor)}>
            {vendor.thumbnail && (
              <img
                src={assetThumb(vendor.thumbnail)}
                alt={vendor.name}
                loading="lazy"
                decoding="async"
                onError={(e) => { e.target.style.display = 'none' }}
              />
            )}
          </S.Tile>
        ))}
      </S.Grid>
    </S.Panel>
  )
}
